import Link from "next/link";
import type { ErrorPageContent } from "@/lib/content/entries/error-pages";
import type { ErrorPageRecovery } from "@/lib/error-pages/recovery-routes";
import { conciseCrawlerHeading, fullHeadingLabel } from "@/lib/seo/headings";
import ErrorPageRecoveryView from "./ErrorPageRecovery";

type ErrorPageViewProps = {
  content: ErrorPageContent;
  onRetry?: () => void;
  digest?: string;
  includeDocumentMetadata?: boolean;
  recovery?: ErrorPageRecovery;
};

export default function ErrorPageView({ content, onRetry, digest, includeDocumentMetadata = false, recovery }: ErrorPageViewProps) {
  const heroTitle = conciseCrawlerHeading(content.title, content.eyebrow);
  const heroTitleLabel = fullHeadingLabel(content.title, heroTitle);
  const primaryAction = recovery?.primaryAction ?? content.primaryAction;
  const secondaryAction = recovery?.secondaryAction ?? content.secondaryAction;

  return (
    <div className="resource-methodology">
      {includeDocumentMetadata ? (
        <>
          <title>{content.metadata.title}</title>
          <meta name="description" content={content.metadata.description} />
          <meta name="robots" content="noindex, nofollow" />
        </>
      ) : null}

      <section className="redesign-hero redesign-hero--inner" aria-labelledby="error-page-title">
        <div className="container">
          <div className="redesign-hero__grid">
            <div className="redesign-hero__copy">
              <p className="marketing-eyebrow">{content.eyebrow}</p>
              <h1 id="error-page-title" aria-label={heroTitleLabel} title={heroTitleLabel}>{heroTitle}</h1>
              <p className="redesign-hero__lead">{content.lead}</p>
              <div className="redesign-actions">
                {onRetry ? (
                  <button type="button" className="marketing-button marketing-button--primary" onClick={onRetry}>
                    {content.retryLabel}
                  </button>
                ) : (
                  <Link href={primaryAction.href} className="marketing-button marketing-button--primary">
                    {primaryAction.label}
                  </Link>
                )}
                <Link href={secondaryAction.href} className="marketing-button marketing-button--secondary">
                  {secondaryAction.label}
                </Link>
              </div>
              {digest ? (
                <p className="redesign-hero__updated">
                  {content.digestLabel}: <code>{digest}</code>
                </p>
              ) : null}
            </div>

            <dl className="redesign-hero__stats" aria-label={content.statsAria}>
              <div className="redesign-stat">
                <dt>{content.statusLabel}</dt>
                <dd>{content.statusCode}</dd>
              </div>
              <div className="redesign-stat">
                <dt>{content.indexingLabel}</dt>
                <dd>{content.noindexValue}</dd>
              </div>
              <div className="redesign-stat">
                <dt>{content.ampLabel}</dt>
                <dd>{content.ampNotAdvertisedValue}</dd>
              </div>
            </dl>
          </div>
        </div>
      </section>

      {recovery ? <ErrorPageRecoveryView content={content} recovery={recovery} /> : <section className="redesign-section redesign-section--muted" aria-labelledby="error-page-suggestions">
        <div className="container">
          <div className="marketing-section-heading">
            <p className="marketing-eyebrow">{content.suggestionsEyebrow}</p>
            <h2 id="error-page-suggestions">{content.suggestionsTitle}</h2>
          </div>
          <div className="redesign-proof-grid">
            {content.suggestions.map((suggestion) => (
              <Link className="redesign-proof-card" href={suggestion.href} key={suggestion.href}>
                <span>{suggestion.label}</span>
                <p>{suggestion.description}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>}
    </div>
  );
}
